import { selectCulture } from '@api/application-configuration';
import { getEnvVars } from '@environment';
import { Store } from '@reduxjs/toolkit';
import { QueryClient } from '@tanstack/react-query';
import { ApplicationConfigurationDto } from '@unistocks/proxy/axios-client';
import { ACCEPT_LANGUAGE_HEADER, APP_HEADER } from '@unistocks/shared/consts';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { Logger } from '@unistocks/shared/services/logger.service';
import { getDeviceLanguage } from '@unistocks/shared/utils/device-utils';
import { isAxiosError } from 'axios';
import { RootState } from '../shared/store';
import { AppActions, selectToken } from '../shared/store/root.slice';
import { authClient, axiosClient } from './axios';

const TENANT_NOT_FOUND = 'Volo.AbpIo.MultiTenancy:010001';

const { appName } = getEnvVars();

export function initAPIInterceptor(store: Store<RootState>, queryClient: QueryClient) {
  const handleResponseError = (error: unknown) => {
    if (isAxiosError(error)) {
      Logger.error(error);

      if (error?.response) {
        const { headers, data } = error.response;
        let errorObj = data;

        try {
          errorObj = JSON.parse(data);
        } catch {
          errorObj = data;
        }

        const shouldClearToken =
          headers['www-authenticate']?.includes('invalid_token') ||
          errorObj?.error === 'invalid_grant' ||
          errorObj?.error?.code === TENANT_NOT_FOUND;

        if (shouldClearToken) {
          store.dispatch(AppActions.setToken());
        }
      }
    }

    return Promise.reject(error);
  };

  axiosClient.interceptors.request.use(
    async request => {
      const rootState = store.getState();

      const token = selectToken(rootState);
      let cultureName = '';
      const appConfig = queryClient.getQueryData<ApplicationConfigurationDto>(APP_CONFIG_QUERY_KEY);

      if (appConfig) {
        cultureName = selectCulture(appConfig) ?? '';
      } else {
        cultureName = getDeviceLanguage().cultureName;
      }

      if (!request.headers.Authorization && token && token.access_token) {
        request.headers.Authorization = `${token.token_type} ${token.access_token}`;
      }

      if (!request.headers[ACCEPT_LANGUAGE_HEADER] && cultureName) {
        request.headers[ACCEPT_LANGUAGE_HEADER] = cultureName;
      }

      if (!request.headers[APP_HEADER]) {
        request.headers[APP_HEADER] = appName;
      }

      return request;
    },
    error => Logger.debug(error)
  );

  axiosClient.interceptors.response.use(response => response, handleResponseError);
  authClient.interceptors.response.use(response => response, handleResponseError);
}
